import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { Link } from "react-router-dom";
import {
  Panel,
  PanelHeader,
  PanelBody,
} from "./../../components/panel/panel.jsx";
import ToggleSwitch from "../../components/CustomComponents/Switchery/switchery.jsx";
import CommonTimeSlotForAllWeek from "../../components/CustomComponents/TimeSchedule/commonTimeSlotForAllWeek.js";
import ScheduleManager from "../../components/CustomComponents/TimeSchedule/weeklySlots.js";
import schedule from "../../components/CustomComponents/TimeSchedule/schedule.js";
import {
  checkIfCommonTimeSlots,
  convertToScheduleFormat,
} from "../../components/CustomComponents/TimeSchedule/scheduleConv.js";
import axiosInstance from "../../config/axiosApi/axiosApi.jsx";
import APIs from "../../Constants/apis.js";
import showErrorToast, {
  showSuccessToast,
} from "../../components/CustomComponents/toast/toast.js";
import Preloader from "../../components/CustomComponents/preLoad/preLoader.js";
import useThemeColor from "../../hooks/useThemeColor";
import useDarkMode from "../../hooks/useDarkMode";
import Select from "react-select";
import DropDownStyles, {
  DropDownStylesDark,
} from "./dropdownStyles.js";
import moment from "moment";

const StaffScheduleManager = () => {
  const CompID = useSelector((state) => state.auth.user).userData?.compID;
  const themeColor = useThemeColor();
  const isDarkMode = useDarkMode();
  const dropdownStyles = isDarkMode ? DropDownStylesDark : DropDownStyles;

  const [searchTerm, setSearchTerm] = useState("");
  const [staffOptions, setStaffOptions] = useState([]);
  const [selectedStaff, setSelectedStaff] = useState(null);
  const [loadingStaff, setLoadingStaff] = useState(false);

  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(false);

  const [regularHours, setRegularHours] = useState(true);
  const [useSameTimeSlots, setUseSameTimeSlots] = useState(true);
  const [commonTimeSlot, setCommonTimeSlot] = useState([
    {
      from: "00:00",
      to: "23:59",
    },
  ]);
  const [commonBreak, setCommonBreak] = useState(null);
  const [schedules, setSchedules] = useState(schedule);

  const resetScheduleState = useCallback(() => {
    setRegularHours(true);
    setUseSameTimeSlots(true);
    setCommonTimeSlot([
      {
        from: "00:00",
        to: "23:59",
      },
    ]);
    setCommonBreak(null);
    const defaultSchedulesWithBreaks = schedule.map((sch) => ({
      ...sch,
      timeSlots: sch.timeSlots.map((slot) => ({ ...slot, break: null })),
    }));
    setSchedules(defaultSchedulesWithBreaks);
  }, []);

  const handleApplyCommonTimeSlotToAll = useCallback(() => {
    setSchedules((prevSchedules) =>
      prevSchedules.map((scheduleItem) => ({
        ...scheduleItem,
        timeSlots: commonTimeSlot.map((slot) => ({
          ...slot,
          break: commonBreak
            ? {
              startBraekTime: commonBreak.startBraekTime,
              breakMinutes: commonBreak.breakMinutes,
              isBreakInclusive: commonBreak.isBreakInclusive || false,
            }
            : null,
        })),
      }))
    );
  }, [commonBreak, commonTimeSlot]);

  const fetchStaffSchedule = useCallback(
    async (userName) => {
      if (!userName || !CompID) return;
      setInitialLoading(true);
      setLoading(true);
      try {
        resetScheduleState();
        const response = await axiosInstance.get(APIs.GET_STAFF_DATA, {
          params: {
            CompID,
            UserName: userName,
          },
        });
        const staff = response.data.data || response.data;

        if (
          staff.weeklySchedule &&
          Array.isArray(staff.weeklySchedule) &&
          staff.weeklySchedule.length > 0
        ) {
          setRegularHours(false);
          const isCommon = checkIfCommonTimeSlots(staff.weeklySchedule);
          if (isCommon) {
            const firstDaySlots = staff.weeklySchedule
              .filter(
                (day) => day.dayName === staff.weeklySchedule[0].dayName
              )
              .map((day) => ({
                from: day.openTime,
                to: day.closeTime,
              }));

            setCommonTimeSlot(firstDaySlots);

            const firstSchedule = staff.weeklySchedule[0];
            if (
              firstSchedule.startBraekTime ||
              (firstSchedule.breakMinutes !== null &&
                firstSchedule.breakMinutes !== undefined)
            ) {
              setCommonBreak({
                startBraekTime: firstSchedule.startBraekTime || "",
                breakMinutes: firstSchedule.breakMinutes || null,
                isBreakInclusive: firstSchedule.isBreakInclusive || false,
              });
            }
          } else {
            setUseSameTimeSlots(false);
            let convertedSchedules = convertToScheduleFormat(
              staff.weeklySchedule
            );

            convertedSchedules = convertedSchedules.map((sch) => {
              const dayEntry = staff.weeklySchedule.find(
                (d) => d.dayName === sch.day
              );
              if (dayEntry && sch.timeSlots.length > 0) {
                sch.timeSlots[0].break =
                  dayEntry.startBraekTime ||
                    (dayEntry.breakMinutes !== null &&
                      dayEntry.breakMinutes !== undefined)
                    ? {
                      startBraekTime: dayEntry.startBraekTime || "",
                      breakMinutes: dayEntry.breakMinutes || null,
                      isBreakInclusive:
                        dayEntry.isBreakInclusive || false,
                    }
                    : null;
              }
              return sch;
            });

            setSchedules(convertedSchedules);
          }
        }
      } catch (err) {
        showErrorToast("Failed to load staff schedule");
      } finally {
        setLoading(false);
        setInitialLoading(false);
      }
    },
    [CompID, resetScheduleState]
  );

  const fetchStaffOptions = useCallback(
    async (query) => {
      if (!CompID) {
        setStaffOptions([]);
        return;
      }

      const trimmedQuery = query?.trim() || "";
      const params = {
        CompID,
        PageSize: 500,
        PageNumber: 1,
        SortBy: "UserName",
        SortOrder: "ASC",
      };

      if (trimmedQuery.length >= 2) {
        params.Search = trimmedQuery;
      }

      setLoadingStaff(true);
      try {
        const response = await axiosInstance.get(APIs.GET_COMPANY_STAFF, {
          params,
        });

        const staffData = response.data.data || response.data || [];
        const mappedOptions = staffData.map((staff) => {
          const fullName = `${staff.firstName || ""} ${staff.lastName || ""}`.trim();
          const label = fullName
            ? `${fullName} (${staff.userName})`
            : staff.userName;
          return {
            value: staff.userName,
            label,
            userName: staff.userName,
            email: staff.email,
          };
        });
        setStaffOptions(mappedOptions);
      } catch (err) {
        setStaffOptions([]);
        showErrorToast("Failed to load staff list");
      } finally {
        setLoadingStaff(false);
      }
    },
    [CompID]
  );

  useEffect(() => {
    fetchStaffOptions(searchTerm);
  }, [searchTerm, fetchStaffOptions]);

  const handleSubmit = useCallback(
    async (e) => {
      e?.preventDefault();
      if (!selectedStaff?.value) {
        showErrorToast("Select a staff member before saving the schedule.");
        return;
      }

      if (!CompID) {
        showErrorToast("Company id is missing.");
        return;
      }

      try {
        setLoading(true);
        const formattedSchedules = [];

        if (!regularHours) {
          if (useSameTimeSlots) {
            const days = [
              "Monday",
              "Tuesday",
              "Wednesday",
              "Thursday",
              "Friday",
              "Saturday",
              "Sunday",
            ];

            if (commonTimeSlot.length > 0) {
              commonTimeSlot.forEach((slot) => {
                days.forEach((day) => {
                  formattedSchedules.push({
                    id: 0,
                    compID: CompID,
                    userName: selectedStaff.value,
                    dayName: day,
                    openTime: slot.from
                      ? moment(slot.from, "HH:mm").format("HH:mm")
                      : null,
                    closeTime: slot.to
                      ? moment(slot.to, "HH:mm").format("HH:mm")
                      : null,
                    startBraekTime: commonBreak?.startBraekTime
                      ? commonBreak.startBraekTime
                      : null,
                    endBraekTime:
                      commonBreak?.startBraekTime && commonBreak.breakMinutes
                        ? moment(
                          commonBreak.startBraekTime,
                          "HH:mm"
                        )
                          .add(commonBreak.breakMinutes, "minutes")
                          .format("HH:mm")
                        : null,
                    breakMinutes: commonBreak?.breakMinutes || null,
                    isBreakInclusive:
                      commonBreak?.isBreakInclusive || false,
                    updateDateTime: new Date().toISOString(),
                  });
                });
              });
            }
          } else {
            schedules.forEach((schedule) => {
              if (schedule.checked && schedule.timeSlots.length > 0) {
                schedule.timeSlots.forEach((slot) => {
                  if (
                    moment(slot.from, "HH:mm").isValid() &&
                    moment(slot.to, "HH:mm").isValid()
                  ) {
                    formattedSchedules.push({
                      id: 0,
                      compID: CompID,
                      userName: selectedStaff.value,
                      dayName: schedule.day,
                      openTime: moment(slot.from, "HH:mm").format("HH:mm"),
                      closeTime: moment(slot.to, "HH:mm").format("HH:mm"),
                      startBraekTime: slot.break?.startBraekTime || null,
                      endBraekTime:
                        slot.break?.startBraekTime &&
                          slot.break.breakMinutes
                          ? moment(slot.break.startBraekTime, "HH:mm")
                            .add(slot.break.breakMinutes, "minutes")
                            .format("HH:mm")
                          : null,
                      breakMinutes: slot.break?.breakMinutes || null,
                      isBreakInclusive: slot.break?.isBreakInclusive || false,
                      updateDateTime: new Date().toISOString(),
                    });
                  }
                });
              }
            });
          }
        }

        const response = await axiosInstance.put(
          APIs.UPDATE_USER_WEEKLY_SCHEDULE,
          formattedSchedules,
          {
            params: {
              UserName: selectedStaff.value,
              CompID,
            },
          }
        );

        if (response.data.status) {
          showSuccessToast("Staff schedule updated successfully.");
        } else {
          showErrorToast(response.data.message || "Failed to update schedule.");
        }
      } catch (err) {
        showErrorToast(
          err.response?.data?.message ||
          "Failed to update staff schedule."
        );
      } finally {
        setLoading(false);
      }
    },
    [
      CompID,
      selectedStaff,
      regularHours,
      useSameTimeSlots,
      commonTimeSlot,
      commonBreak,
      schedules,
    ]
  );

  const selectedStaffLabel = selectedStaff?.label || null;

  return (
    <div>
      {(loading || initialLoading) && <Preloader />}

      <div className="d-flex align-items-center mb-3 mt-2">
        <div>
          <ol className="breadcrumb">
            <li className="breadcrumb-item">
              <Link to="/">Home</Link>
            </li>
            <li className="breadcrumb-item active">Staff Schedule</li>
          </ol>
          <h1 className="page-header mb-0"> Schedule Planner</h1>
        </div>
      </div>

      <div className="card border-0">
        <Panel className="m-3">
          <PanelHeader>Choose Staff Member</PanelHeader>
          <PanelBody>
            <div className="row g-3">
              <div className="col-lg-6 col-md-8">
                <Select
                  placeholder="Select Staff Member"
                  onInputChange={(value) => setSearchTerm(value)}
                  options={staffOptions}
                  isLoading={loadingStaff}
                  value={selectedStaff}
                  onChange={(option) => {
                    setSelectedStaff(option);
                    if (option?.value) {
                      fetchStaffSchedule(option.value);
                    } else {
                      resetScheduleState();
                    }
                  }}
                  styles={dropdownStyles}
                  isClearable
                  menuPortalTarget={document.body}
                  menuPosition="fixed"
                />
              </div>
            </div>
          </PanelBody>
        </Panel>

        {selectedStaff && (
          <Panel className="m-3">
            <PanelHeader>
              {selectedStaffLabel ? `Schedule for ${selectedStaffLabel}` : "Staff Schedule"}
            </PanelHeader>
            <PanelBody>
              <div className="d-flex justify-content-between align-items-center mb-3">
                <div>
                  <p className="mb-0 text-muted">
                    Adjust availability for{" "}
                    <strong>{selectedStaff?.label}</strong>. Toggle off
                    "Official Business Hours" to customise
                    day-by-day slots.
                  </p>
                </div>
                <button
                  className="btn px-4"
                  style={{
                    backgroundColor: themeColor,
                    background: `linear-gradient(135deg, ${themeColor} 0%, ${themeColor}dd 100%)`,
                    border: "none",
                    color: "white",
                    borderRadius: "8px",
                    padding: "8px 16px",
                    fontSize: "14px",
                    fontWeight: "600",
                    transition: "all 0.3s ease",
                    boxShadow: `0 2px 8px ${themeColor}40`,
                  }}
                  onClick={handleSubmit}
                  onMouseEnter={(e) => {
                    e.target.style.transform = "translateY(-2px)";
                    e.target.style.boxShadow = `0 4px 12px ${themeColor}60`;
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.transform = "translateY(0)";
                    e.target.style.boxShadow = `0 2px 8px ${themeColor}40`;
                  }}
                >
                  <i className="fa fa-save fa-lg me-2 ms-n2 text-white"></i>
                  Save Schedule
                </button>
              </div>

              <div className="row mb-3">
                <div className="col-12 col-md-8">
                  <ToggleSwitch
                    isChecked={regularHours}
                    onToggle={(checked) => setRegularHours(checked)}
                    label={"Official Business Hours"}
                    disabled={false}
                    themeColor={themeColor}
                  />
                </div>
              </div>

              {!regularHours && (
                <div className="row m-3">
                  <div className="mb-3">
                    <ToggleSwitch
                      isChecked={useSameTimeSlots}
                      onToggle={(checked) => {
                        setUseSameTimeSlots(checked);
                        if (!checked) {
                          handleApplyCommonTimeSlotToAll();
                        }
                      }}
                      label={"Use same time slots for all days"}
                      disabled={false}
                      themeColor={themeColor}
                    />
                  </div>
                  {useSameTimeSlots ? (
                    <CommonTimeSlotForAllWeek
                      setCommonTimeSlots={setCommonTimeSlot}
                      commonTimeSlots={commonTimeSlot}
                      isStaffSchedule={true}
                      commonBreak={commonBreak}
                      setCommonBreak={setCommonBreak}
                    />
                  ) : (
                    <ScheduleManager
                      schedules={schedules}
                      setSchedules={setSchedules}
                      themeColor={themeColor}
                      isDarkMode={isDarkMode}
                      isStaffSchedule={true}
                    />
                  )}
                </div>
              )}
            </PanelBody>
          </Panel>
        )}

        {!selectedStaff && (
          <Panel className="m-3">
            <PanelBody>
              <p className="text-muted mb-0">
                Search and select a team member to view or update their weekly
                schedule.
              </p>
            </PanelBody>
          </Panel>
        )}
      </div>
    </div>
  );
};

export default StaffScheduleManager;

